/**
 * Admin router - platform management
 * @package @app/api
 */

import { ORPCError } from '@orpc/server';
import { z } from 'zod';
import { writeAuditLog } from '../lib/audit';
import { adminProcedure } from '../middleware/auth';

import { PLAN_PRICES } from '@app/billing-config';

function estimateMrr(
  subs: ReadonlyArray<{
    plan: string | null;
    seats: number;
    status: string | null;
    billingInterval?: string | null;
  }>,
): number {
  return subs
    .filter((s) => s.status === 'active')
    .reduce((total, s) => {
      if (!s.plan) return total;
      const prices = PLAN_PRICES[s.plan];
      if (!prices) return total;
      const isAnnual = s.billingInterval === 'year' || s.billingInterval === 'annual';
      // For annual subs, normalize the annual price to a monthly figure.
      // If prices.annual is undefined for this plan, the plan doesn't offer
      // an annual variant — treat as monthly so MRR isn't double-counted.
      const perSeatMonthly = isAnnual && prices.annual !== undefined
        ? prices.annual / 12
        : prices.monthly;
      return total + perSeatMonthly * (s.seats || 1);
    }, 0);
}

export const adminRouter = {
  dashboard: adminProcedure.handler(async () => {
    const { prisma } = await import('@app/db');

    const [
      totalAthletes,
      activeDeals,
      universities,
      dealVolume,
      avgDealResult,
      aiExtractions,
      pendingPayments,
      activeSubs,
    ] = await Promise.all([
      prisma.user.count({ where: { userType: 'athlete' } }),
      prisma.deal.count({ where: { status: 'ACTIVE' } }),
      prisma.organization.count({ where: { type: 'university' } }),
      prisma.deal.aggregate({ _sum: { amount: true } }),
      prisma.deal.aggregate({ _avg: { amount: true } }),
      prisma.deal.count({ where: { aiExtracted: true } }),
      prisma.payment.aggregate({
        where: { status: { in: ['PENDING', 'OVERDUE'] } },
        _sum: { amount: true },
      }),
      prisma.subscription.findMany({
        where: { status: 'active' },
        select: { plan: true, seats: true, status: true, billingInterval: true },
      }),
    ]);

    return {
      totalAthletes,
      activeDeals,
      universities,
      mrr: estimateMrr(activeSubs),
      dealVolume: Number(dealVolume._sum.amount ?? 0),
      avgDealSize: Number(avgDealResult._avg.amount ?? 0),
      aiExtractions,
      pendingPayments: Number(pendingPayments._sum.amount ?? 0),
    };
  }),

  users: adminProcedure
    .input(
      z.object({
        search: z.string().optional(),
        page: z.number().int().positive().default(1),
        limit: z.number().int().positive().max(100).default(20),
      }),
    )
    .handler(async ({ input }) => {
      const { prisma } = await import('@app/db');
      const { search, page, limit } = input;

      const where = search
        ? {
            OR: [
              { name: { contains: search, mode: 'insensitive' as const } },
              { email: { contains: search, mode: 'insensitive' as const } },
            ],
          }
        : {};

      const [users, total] = await Promise.all([
        prisma.user.findMany({
          where,
          orderBy: { createdAt: 'desc' },
          skip: (page - 1) * limit,
          take: limit,
          select: {
            id: true,
            name: true,
            email: true,
            image: true,
            role: true,
            sport: true,
            school: true,
            createdAt: true,
            _count: { select: { deals: true } },
          },
        }),
        prisma.user.count({ where }),
      ]);

      return {
        users: users.map((u) => ({
          ...u,
          dealCount: u._count.deals,
          createdAt: u.createdAt.toISOString(),
        })),
        total,
        page,
        totalPages: Math.ceil(total / limit),
      };
    }),

  deals: adminProcedure
    .input(
      z.object({
        status: z.string().optional(),
        search: z.string().optional(),
        page: z.number().int().positive().default(1),
        limit: z.number().int().positive().max(100).default(20),
      }),
    )
    .handler(async ({ input }) => {
      const { prisma } = await import('@app/db');
      const { status, search, page, limit } = input;

      const where: Record<string, unknown> = {};
      if (status) where.status = status;
      if (search) {
        where.OR = [
          { title: { contains: search, mode: 'insensitive' } },
          { brandName: { contains: search, mode: 'insensitive' } },
        ];
      }

      const [deals, total, statusCounts] = await Promise.all([
        prisma.deal.findMany({
          where,
          orderBy: { createdAt: 'desc' },
          skip: (page - 1) * limit,
          take: limit,
          include: {
            athlete: { select: { name: true, email: true } },
          },
        }),
        prisma.deal.count({ where }),
        Promise.all([
          prisma.deal.count(),
          prisma.deal.count({ where: { status: 'ACTIVE' } }),
          prisma.deal.count({ where: { status: 'DRAFT' } }),
          prisma.deal.count({ where: { status: 'COMPLETED' } }),
          prisma.deal.count({ where: { status: { in: ['EXPIRED', 'CANCELLED'] } } }),
        ]),
      ]);

      return {
        deals: deals.map((d) => ({
          id: d.id,
          title: d.title,
          brandName: d.brandName,
          amount: Number(d.amount),
          status: d.status,
          athleteName: d.athlete.name,
          athleteEmail: d.athlete.email,
          createdAt: d.createdAt.toISOString(),
          endDate: d.endDate?.toISOString() ?? null,
        })),
        total,
        page,
        totalPages: Math.ceil(total / limit),
        statusCounts: {
          all: statusCounts[0],
          active: statusCounts[1],
          draft: statusCounts[2],
          completed: statusCounts[3],
          expiredCancelled: statusCounts[4],
        },
      };
    }),

  brands: adminProcedure
    .input(
      z.object({
        industry: z.string().optional(),
        search: z.string().optional(),
        page: z.number().int().positive().default(1),
        limit: z.number().int().positive().max(100).default(20),
      }),
    )
    .handler(async ({ input }) => {
      const { prisma } = await import('@app/db');
      const { industry, search, page, limit } = input;

      const where: Record<string, unknown> = {};
      if (industry) where.industry = industry;
      if (search) {
        where.name = { contains: search, mode: 'insensitive' };
      }

      const [brands, total] = await Promise.all([
        prisma.brand.findMany({
          where,
          orderBy: { totalDeals: 'desc' },
          skip: (page - 1) * limit,
          take: limit,
        }),
        prisma.brand.count({ where }),
      ]);

      return {
        brands: brands.map((b) => ({
          ...b,
          avgDealSize: b.avgDealSize ? Number(b.avgDealSize) : null,
          createdAt: b.createdAt.toISOString(),
          updatedAt: b.updatedAt.toISOString(),
        })),
        total,
        page,
        totalPages: Math.ceil(total / limit),
      };
    }),

  organizations: adminProcedure
    .input(
      z.object({
        search: z.string().optional(),
        page: z.number().int().positive().default(1),
      }),
    )
    .handler(async ({ input }) => {
      const { prisma } = await import('@app/db');
      const { search, page } = input;
      const limit = 20;

      const where = search ? { name: { contains: search, mode: 'insensitive' as const } } : {};

      const [orgs, total] = await Promise.all([
        prisma.organization.findMany({
          where,
          orderBy: { createdAt: 'desc' },
          skip: (page - 1) * limit,
          take: limit,
          include: { _count: { select: { members: true, deals: true } } },
        }),
        prisma.organization.count({ where }),
      ]);

      return {
        organizations: orgs.map((o) => ({
          id: o.id,
          name: o.name,
          slug: o.slug,
          type: o.type,
          logo: o.logo,
          memberCount: o._count.members,
          dealCount: o._count.deals,
          createdAt: o.createdAt.toISOString(),
        })),
        total,
        page,
        totalPages: Math.ceil(total / limit),
      };
    }),

  auditLogs: adminProcedure
    .input(
      z.object({
        action: z.string().optional(),
        userId: z.string().optional(),
        page: z.number().int().positive().default(1),
      }),
    )
    .handler(async ({ input }) => {
      const { prisma } = await import('@app/db');
      const { action, userId, page } = input;
      const limit = 50;

      const where: Record<string, unknown> = {};
      if (action) where.action = action;
      if (userId) where.actorId = userId;

      const [logs, total] = await Promise.all([
        prisma.auditLog.findMany({
          where,
          orderBy: { timestamp: 'desc' },
          skip: (page - 1) * limit,
          take: limit,
        }),
        prisma.auditLog.count({ where }),
      ]);

      return {
        logs: logs.map((l) => ({
          ...l,
          timestamp: l.timestamp.toISOString(),
        })),
        total,
        page,
        totalPages: Math.ceil(total / limit),
      };
    }),

  subscriptions: adminProcedure
    .input(
      z.object({
        status: z.string().optional(),
        page: z.number().int().positive().default(1),
      }),
    )
    .handler(async ({ input }) => {
      const { prisma } = await import('@app/db');
      const { status, page } = input;
      const limit = 20;

      const where: Record<string, unknown> = {};
      if (status) where.status = status;

      const [subscriptions, total, activeCount, activeSubs] = await Promise.all([
        prisma.subscription.findMany({
          where,
          orderBy: { createdAt: 'desc' },
          skip: (page - 1) * limit,
          take: limit,
        }),
        prisma.subscription.count({ where }),
        prisma.subscription.count({ where: { status: 'active' } }),
        prisma.subscription.findMany({
          where: { status: 'active' },
          select: { plan: true, seats: true, status: true },
        }),
      ]);

      return {
        subscriptions: subscriptions.map((s) => ({
          ...s,
          createdAt: s.createdAt.toISOString(),
          updatedAt: s.updatedAt.toISOString(),
          periodStart: s.periodStart?.toISOString() ?? null,
          periodEnd: s.periodEnd?.toISOString() ?? null,
          trialStart: s.trialStart?.toISOString() ?? null,
          trialEnd: s.trialEnd?.toISOString() ?? null,
        })),
        total,
        page,
        totalPages: Math.ceil(total / limit),
        activeCount,
        estimatedMrr: estimateMrr(activeSubs),
      };
    }),

  siteConfig: adminProcedure.handler(async () => {
    const { prisma } = await import('@app/db');
    const configs = await prisma.siteConfig.findMany();
    return configs.reduce(
      (acc, c) => {
        acc[c.key] = c.value;
        return acc;
      },
      {} as Record<string, string>,
    );
  }),

  updateSiteConfig: adminProcedure
    .input(z.object({ key: z.string(), value: z.string() }))
    .handler(async ({ input }) => {
      const { prisma } = await import('@app/db');
      await prisma.siteConfig.upsert({
        where: { key: input.key },
        update: { value: input.value },
        create: { key: input.key, value: input.value },
      });
      return { success: true };
    }),

  /**
   * Comp a plan to a user without going through Stripe.
   *
   * Exists so a pilot customer can be given real access before their
   * billing path is wired — the agent tier's checkout ships in a later
   * PR, but the pilot agent needs the product now.
   *
   * Writes a Subscription row with `stripeSubscriptionId: null`. That is
   * safe and intentional:
   *   - the column is `String? @unique`, and Postgres permits many NULLs
   *     in a unique index, so multiple comped rows never collide;
   *   - `findActiveSubscription` filters on `status` only and does not
   *     require a Stripe id, so a comped row resolves exactly like a paid
   *     one for both feature gating and rate-limit tiering.
   *
   * Reversible: pass `plan: 'free'` to revoke (marks any comped row
   * cancelled). Stripe-backed rows are never touched — this only ever
   * manages rows it created, identified by the null Stripe id.
   */
  grantPlan: adminProcedure
    .input(
      z.object({
        userId: z.string(),
        plan: z.string().min(1),
        /** Months of access. Default a year — long enough for a pilot. */
        months: z.number().int().positive().max(60).default(12),
      }),
    )
    .handler(async ({ input, context }) => {
      const { prisma, createId } = await import('@app/db');

      const target = await prisma.user.findUnique({
        where: { id: input.userId },
        select: { id: true, email: true, name: true },
      });
      if (!target) throw new ORPCError('NOT_FOUND', { message: 'User not found' });

      // Only ever touch comped rows (null Stripe id). A real Stripe
      // subscription must be managed in Stripe, never overwritten here.
      const existingComped = await prisma.subscription.findFirst({
        where: {
          referenceId: input.userId,
          referenceType: 'user',
          stripeSubscriptionId: null,
        },
        orderBy: { createdAt: 'desc' },
      });

      const now = new Date();
      const periodEnd = new Date(now);
      periodEnd.setMonth(periodEnd.getMonth() + input.months);

      // 'free' is the revoke path — cancel the comp rather than granting.
      const revoking = input.plan === 'free';

      if (existingComped) {
        await prisma.subscription.update({
          where: { id: existingComped.id },
          data: revoking
            ? { status: 'canceled', canceledAt: now, endedAt: now }
            : {
                plan: input.plan,
                status: 'active',
                periodStart: now,
                periodEnd,
                canceledAt: null,
                endedAt: null,
                cancelAtPeriodEnd: false,
              },
        });
      } else if (!revoking) {
        await prisma.subscription.create({
          data: {
            id: createId(),
            referenceId: input.userId,
            referenceType: 'user',
            plan: input.plan,
            status: 'active',
            periodStart: now,
            periodEnd,
            stripeSubscriptionId: null,
          },
        });
      }

      await writeAuditLog({
        action: revoking ? 'admin.plan_revoked' : 'admin.plan_granted',
        category: 'billing',
        actor: {
          id: context.session.user.id,
          name: context.session.user.name,
          email: context.session.user.email,
          role: context.session.user.role ?? null,
        },
        target: { type: 'user', id: target.id, name: target.name },
        context: { plan: input.plan, months: input.months, comped: true },
      });

      return { success: true, plan: revoking ? 'free' : input.plan };
    }),
};
